import React, { useRef } from 'react';
import { Box, Button, IconButton } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { useReactToPrint } from 'react-to-print';
import dayjs from 'dayjs';

// Print Component
interface PrintableTokenListProps {
    dataGridArray: any[];
    header: Record<string, string>;
    startDate: any;
    endDate: any;
    toHeaderName: (field: string) => string;
}

const PrintableTokenList = React.forwardRef<HTMLDivElement, PrintableTokenListProps>(
    ({ dataGridArray, header, startDate, endDate, toHeaderName }, ref) => {
        return (
            <div ref={ref} style={{ padding: '20px' }}>
                <style>
                    {`
                        @media print {
                        @page {
                            size: A4 landscape;
                            margin: 10mm;
                        }
                        
                        body {
                            print-color-adjust: exact;
                            -webkit-print-color-adjust: exact;
                        }

                        .print-container {
                            width: 100%;
                        }

                        .print-header {
                            text-align: center;
                            margin-bottom: 15px;
                        }

                        .print-header h1 {
                            margin: 0;
                            font-size: 22px;
                            color: #1a5f23;
                        }

                        .print-info {
                            display: flex;
                            justify-content: space-between;
                            margin-bottom: 10px;
                            font-size: 11px;
                        }

                        .print-table {
                            width: 100%;
                            border-collapse: collapse;
                            font-size: 9px;
                            line-height: 1.1;
                        }

                        .print-table th {
                            background-color: #1a5f23 !important;
                            color: white !important;
                            padding: 4px 2px;
                            text-align: center;
                            border: 1px solid #ddd;
                            font-weight: bold;
                            line-height: 1.2;
                            height: 28px;
                        }

                        .print-table td {
                            padding: 3px 2px;
                            border: 1px solid #ddd;
                            text-align: center;
                            line-height: 1.1;
                            height: 24px;
                            vertical-align: middle;
                        }

                        .print-table tr:nth-child(even) {
                            background-color: #f9f9f9;
                        }

                        .status-chip {
                            display: inline-block;
                            padding: 1px 6px;
                            border-radius: 10px;
                            font-size: 8px;
                            font-weight: bold;
                            line-height: 1;
                        }

                        .status-draft {
                            background-color: #e0e0e0;
                            color: #666;
                        }

                        .status-submitted {
                            background-color: #4caf50;
                            color: white;
                        }

                        .status-cancelled {
                            background-color: #f44336;
                            color: white;
                        }

                        .token-chip {
                            display: inline-block;
                            background-color: #1976d2;
                            color: white;
                            padding: 1px 6px;
                            border-radius: 10px;
                            font-weight: bold;
                            font-size: 9px;
                            line-height: 1;
                        }
                        }
                    `}
                </style>

                <div className="print-container">
                    <div className="print-header">
                        <h1>Token Management Report</h1>
                    </div>

                    <div className="print-info">
                        <div>
                            <strong>Start Date:</strong> {startDate ? dayjs(startDate).format('DD/MM/YYYY') : 'N/A'}
                        </div>
                        <div>
                            <strong>End Date:</strong> {endDate ? dayjs(endDate).format('DD/MM/YYYY') : 'N/A'}
                        </div>
                        <div>
                            <strong>Total Records:</strong> {dataGridArray.length}
                        </div>
                        <div>
                            <strong>Print Date:</strong> {dayjs().format('DD/MM/YYYY HH:mm')}
                        </div>
                    </div>

                    <table className="print-table">
                        <thead>
                            <tr>
                                <th style={{ width: '3%' }}>#</th>
                                {Object.keys(header)
                                    .filter(field => !['name', 'owner', 'creation', 'modified', 'modified_by', 'doctype', 'idx'].includes(field))
                                    .map((field) => (
                                        <th key={field}>{header[field]}</th>
                                    ))}
                            </tr>
                        </thead>
                        <tbody>
                            {dataGridArray.map((row, index) => (
                                <tr key={row.name || index}>
                                    <td>{index + 1}</td>
                                    {Object.keys(header)
                                        .filter(field => !['name', 'owner', 'creation', 'modified', 'modified_by', 'doctype', 'idx'].includes(field))
                                        .map((field) => {
                                            const value = row[field];

                                            // Handle empty values
                                            if (value === null || value === undefined || value === '') {
                                                return <td key={field}>---</td>;
                                            }

                                            // Format token number
                                            if (field === 'token_number') {
                                                return (
                                                    <td key={field}>
                                                        <span className="token-chip">{value}</span>
                                                    </td>
                                                );
                                            }

                                            // Format status
                                            if (field === 'docstatus') {
                                                const statusConfig: Record<number, { label: string; className: string }> = {
                                                    0: { label: 'Draft', className: 'status-draft' },
                                                    1: { label: 'Submitted', className: 'status-submitted' },
                                                    2: { label: 'Cancelled', className: 'status-cancelled' },
                                                };
                                                const config = statusConfig[value] || { label: 'Unknown', className: 'status-draft' };
                                                return (
                                                    <td key={field}>
                                                        <span className={`status-chip ${config.className}`}>
                                                            {config.label}
                                                        </span>
                                                    </td>
                                                );
                                            }

                                            // Format date/time
                                            if (field === 'in_time' || field === 'out_time') {
                                                return (
                                                    <td key={field}>
                                                        {dayjs(value).format('MMM DD, YYYY HH:mm')}
                                                    </td>
                                                );
                                            }

                                            return <td key={field}>{String(value)}</td>;
                                        })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }
);

PrintableTokenList.displayName = 'PrintableTokenList';

// Hook to use in your TokenList component
export const usePrintTokenList = (
    dataGridArray: any[],
    header: Record<string, string>,
    startDate: any,
    endDate: any,
    toHeaderName: (field: string) => string
) => {
    const printRef = useRef<HTMLDivElement>(null);

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: `Token_Management_${dayjs().format('YYYY-MM-DD_HH-mm')}`,
        pageStyle: `
      @page {
        size: A4 landscape;
        margin: 10mm;
      }
    `,
    });

    const PrintButton = () => (
        <>
            <Button
                variant="outlined"
                color="primary"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
                sx={{ textTransform: 'none' }}
            >
                Print Report
            </Button>
            <div style={{ display: 'none' }}>
                <PrintableTokenList
                    ref={printRef}
                    dataGridArray={dataGridArray}
                    header={header}
                    startDate={startDate}
                    endDate={endDate}
                    toHeaderName={toHeaderName}
                />
            </div>
        </>
    );

    const PrintIconButton = () => (
        <>
            <IconButton
                color="primary"
                onClick={handlePrint}
                title="Print Report"
            >
                <PrintIcon />
            </IconButton>
            <div style={{ display: 'none' }}>
                <PrintableTokenList
                    ref={printRef}
                    dataGridArray={dataGridArray}
                    header={header}
                    startDate={startDate}
                    endDate={endDate}
                    toHeaderName={toHeaderName}
                />
            </div>
        </>
    );

    return { PrintButton, PrintIconButton, handlePrint };
};

export default PrintableTokenList;